import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Select } from 'antd';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowDown, ArrowUp, BookOpen, GraduationCap, Loader2, MapPin, Users } from 'lucide-react';
import { PageHeader } from '@/shared/components';
import { KpiCard } from '@/features/budgets/components/KpiCard';
import { BAND_KIND_LABELS, type BandKind } from '@/features/reports/data/regionalComparisonsMock';
import type {
  GovernorateBandTableRow,
  GovernorateComparisonRow,
  RegionalBandCountDto,
} from '@/features/reports/api';
import {
  buildGovernorateBandTableRows,
  mapRegionSchoolStatisticsToRows,
  weightedNationalAvgTuition,
} from '@/features/reports/api';
import { useRegionalComparisonsQuery } from '@/features/reports/hooks/useRegionalComparisonsQuery';
import { useBudgetReportYearsListQuery } from '@/features/budgets/hooks/useBudgetQueries';
import { useDefaultYear } from '@/features/settings/hooks/useDashboardSettingsQuery';
import {
  useAreasForCitiesQuery,
  useCitiesForGovernoratesQuery,
  useGovernoratesQuery,
} from '@/features/region/hooks/useRegionQueries';
import { Building2Icon, CheckCircleIcon, DollarSignIcon } from '@/shared/ui/Icons';

type SortKey =
  | 'governorateName'
  | 'schoolsCount'
  | 'studentsCount'
  | 'avgTuitionUsdWeighted'
  | 'minTuitionUsd'
  | 'maxTuitionUsd'
  | 'budgetSubmittedRatePct';

type SortDir = 'asc' | 'desc';

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtInt(n: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(n));
}

function fmtPct1(n: number): string {
  return `${n.toFixed(1)}%`;
}

/** Backend may send invalid % for unassigned governorates; hide obvious outliers. */
function fmtGovernorateStudentSharePct(n: number): string {
  if (!Number.isFinite(n) || n < 0 || n > 100) return '—';
  return fmtPct1(n);
}

function sharePct(part: number, whole: number): number | null {
  if (!Number.isFinite(part) || !Number.isFinite(whole) || whole <= 0) return null;
  return (part / whole) * 100;
}

function buildBandKpiDetail(bands: RegionalBandCountDto[], schoolCount: number) {
  if (bands.length === 0) {
    return <p className="text-slate-500">No band data for this view.</p>;
  }
  return (
    <dl className="max-h-36 space-y-1.5 overflow-y-auto leading-relaxed pr-1">
      {bands.map((b) => {
        const pct = sharePct(b.numberOfSchools, schoolCount);
        return (
          <div key={b.name} className="flex justify-between gap-2">
            <dt className="min-w-0 truncate text-slate-500" title={b.name} dir="auto">
              {b.name}
            </dt>
            <dd className="shrink-0 text-right font-mono tabular-nums text-slate-800">
              {fmtInt(b.numberOfSchools)}
              {b.numberOfStudents > 0 ? ` · ${fmtInt(b.numberOfStudents)} st.` : ''}
              {pct != null ? ` (${fmtPct1(pct)})` : ''}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function bandKpiHeadline(bands: RegionalBandCountDto[]): string {
  const total = bands.reduce((sum, b) => sum + b.numberOfSchools, 0);
  return total > 0 ? fmtInt(total) : '—';
}

function compareRows(
  a: GovernorateComparisonRow,
  b: GovernorateComparisonRow,
  key: SortKey,
  dir: SortDir
): number {
  const m = dir === 'asc' ? 1 : -1;
  if (key === 'governorateName') {
    return a.governorateName.localeCompare(b.governorateName, undefined, { sensitivity: 'base' }) * m;
  }
  const va = a[key] as number;
  const vb = b[key] as number;
  return (va - vb) * m;
}

type BandSortKey =
  | 'governorateName'
  | 'bandLabel'
  | 'studentsCount'
  | 'studentShareOfGovernoratePct'
  | 'schoolsCount'
  | 'avgTuitionUsdWeighted'
  | 'budgetSubmittedRatePct'
  | 'complianceRatePct';

function compareBandRows(
  a: GovernorateBandTableRow,
  b: GovernorateBandTableRow,
  key: BandSortKey,
  dir: SortDir
): number {
  const m = dir === 'asc' ? 1 : -1;
  if (key === 'governorateName') {
    const c = a.governorateName.localeCompare(b.governorateName, undefined, { sensitivity: 'base' });
    if (c !== 0) return c * m;
    return a.bandLabel.localeCompare(b.bandLabel, undefined, { sensitivity: 'base' }) * m;
  }
  if (key === 'bandLabel') {
    return a.bandLabel.localeCompare(b.bandLabel, undefined, { sensitivity: 'base' }) * m;
  }
  const va = a[key] as number;
  const vb = b[key] as number;
  return (va - vb) * m;
}

function RegionalComparisonsYearLanding() {
  return (
    <section className="mt-6 rounded-2xl border border-indigo-200/60 bg-gradient-to-br from-indigo-50/40 via-white to-slate-50/90 px-6 py-12 shadow-md ring-1 ring-indigo-100/80 sm:px-10 sm:py-14">
      <div className="mx-auto max-w-2xl text-center">
        <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200/80">
          <BookOpen className="size-7" strokeWidth={1.5} aria-hidden />
        </div>
        <h2 className="mt-6 text-xl font-bold tracking-tight text-slate-900">Select a reporting year</h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          Choose the reporting year above to load governorate indicators, classification breakdowns, and financial
          summaries for the private school sector.
        </p>
      </div>
    </section>
  );
}

export function RegionalComparisonsPage() {
  const [year, setYear] = useState<string>('');
  const [governorateIds, setGovernorateIds] = useState<string[]>([]);
  const [cityIds, setCityIds] = useState<string[]>([]);
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('governorateName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [bandKind, setBandKind] = useState<BandKind>('enrollment');
  const [bandSortKey, setBandSortKey] = useState<BandSortKey>('governorateName');
  const [bandSortDir, setBandSortDir] = useState<SortDir>('asc');
  const reportYearsQuery = useBudgetReportYearsListQuery();
  const defaultYear = useDefaultYear();
  const yearSelected = Boolean(year.trim());

  useEffect(() => {
    if (!year && defaultYear) setYear(defaultYear);
  }, [defaultYear]);

  const budgetYearOptions = useMemo(() => {
    const raw = reportYearsQuery.data ?? [];
    return [...raw].sort((a, b) => b - a).map((n) => ({ id: String(n), label: String(n) }));
  }, [reportYearsQuery.data]);

  const governoratesQuery = useGovernoratesQuery();
  const citiesQuery = useCitiesForGovernoratesQuery(governorateIds);
  const areasQuery = useAreasForCitiesQuery(
    cityIds,
    yearSelected && governorateIds.length > 0
  );

  const governorateSelectOptions = useMemo(
    () =>
      (governoratesQuery.data ?? []).map((g) => ({
        label: g.name,
        value: String(g.id),
      })),
    [governoratesQuery.data]
  );

  const citySelectOptions = useMemo(
    () =>
      (citiesQuery.data ?? []).map((c) => ({
        label: c.name,
        value: String(c.id),
      })),
    [citiesQuery.data]
  );

  const areaSelectOptions = useMemo(
    () =>
      (areasQuery.data ?? []).map((a) => ({
        label: a.name,
        value: String(a.id),
      })),
    [areasQuery.data]
  );

  useEffect(() => {
    if (governorateIds.length === 0) {
      setCityIds([]);
      setAreaIds([]);
      return;
    }
    const allowed = new Set(citySelectOptions.map((o) => o.value));
    setCityIds((prev) => prev.filter((id) => allowed.has(id)));
  }, [governorateIds.length, citySelectOptions]);

  useEffect(() => {
    if (cityIds.length === 0) {
      setAreaIds([]);
      return;
    }
    const allowed = new Set(areaSelectOptions.map((o) => o.value));
    setAreaIds((prev) => prev.filter((id) => allowed.has(id)));
  }, [cityIds.length, areaSelectOptions]);

  const apiYear = useMemo(() => {
    const n = Number.parseInt(year.trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [year]);

  const reportQuery = useRegionalComparisonsQuery({
    year: yearSelected ? apiYear : null,
    governorateIds,
    cityIds,
    areaIds,
  });

  const report = reportQuery.data;
  const isReportLoading = reportQuery.isLoading || (reportQuery.isFetching && !report);

  const governorateRows = useMemo(
    () => mapRegionSchoolStatisticsToRows(report?.regionSchoolStatistics ?? []),
    [report?.regionSchoolStatistics]
  );

  const nationalAvgTuition = useMemo(
    () => weightedNationalAvgTuition(report?.regionSchoolStatistics ?? []),
    [report?.regionSchoolStatistics]
  );

  const sortedRows = useMemo(() => {
    if (!governorateRows.length) return [];
    const copy = [...governorateRows];
    copy.sort((a, b) => compareRows(a, b, sortKey, sortDir));
    return copy;
  }, [governorateRows, sortKey, sortDir]);

  const toggleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else {
        setSortKey(key);
        setSortDir(key === 'governorateName' ? 'asc' : 'desc');
      }
    },
    [sortKey]
  );

  const bandCrossTabSource = useMemo(() => {
    if (!report) return [];
    if (bandKind === 'tuition') return report.tuitionCrossTab;
    if (bandKind === 'parent') return report.parentTuitionCrossTab;
    return report.schoolSizeCrossTab;
  }, [report, bandKind]);

  const bandTableSectionCopy = useMemo(() => {
    if (bandKind === 'tuition') {
      return {
        title: 'Tuition bands by governorate',
        description: 'Distribution of students and schools across tuition clusters. Totals appear after each governorate.',
      };
    }
    if (bandKind === 'parent') {
      return {
        title: 'Parent fee bands by governorate',
        description: 'Distribution of students and schools across parent-fee categories. Totals appear after each governorate.',
      };
    }
    return {
      title: 'Enrollment size bands by governorate',
      description: 'Distribution of students and schools across school-size bands. Totals appear after each governorate.',
    };
  }, [bandKind]);

  const bandTableRows = useMemo(
    () =>
      buildGovernorateBandTableRows(bandCrossTabSource, (a, b) =>
        compareBandRows(a, b, bandSortKey, bandSortDir)
      ),
    [bandCrossTabSource, bandSortKey, bandSortDir]
  );

  const showBandComplianceColumn = useMemo(
    () => bandTableRows.some((r) => !r.isSummary && r.complianceRatePct > 0),
    [bandTableRows]
  );

  const toggleBandSort = useCallback(
    (key: BandSortKey) => {
      if (bandSortKey === key) setBandSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else {
        setBandSortKey(key);
        setBandSortDir(key === 'governorateName' || key === 'bandLabel' ? 'asc' : 'desc');
      }
    },
    [bandSortKey]
  );

  const chartTuitionData = useMemo(
    () =>
      [...sortedRows]
        .sort((a, b) => b.avgTuitionUsdWeighted - a.avgTuitionUsdWeighted)
        .map((r) => ({
          name: r.governorateName,
          avgTuition: r.avgTuitionUsdWeighted,
        })),
    [sortedRows]
  );

  const chartStudentsData = useMemo(
    () =>
      sortedRows.map((r) => ({
        name: r.governorateName.length > 14 ? `${r.governorateName.slice(0, 12)}…` : r.governorateName,
        fullName: r.governorateName,
        students: r.studentsCount,
      })),
    [sortedRows]
  );

  const avgTeacherSalaryUsd = useMemo(() => {
    if (!report || report.totalTeacherCount <= 0) return null;
    return report.totalSalaries / report.totalTeacherCount;
  }, [report]);

  const teacherCountKpiDetail = useMemo(() => {
    if (!report) return null;
    const rows = [
      { label: 'Permanent', count: report.totalPermTeacherCount },
      { label: 'Contractual', count: report.totalContTeacherCount },
      { label: 'Administrative', count: report.totalAdminTeacherCount },
    ];
    return (
      <dl className="space-y-1.5 leading-relaxed">
        {rows.map((r) => (
          <div key={r.label} className="flex justify-between gap-3">
            <dt className="shrink-0 text-slate-500">{r.label}</dt>
            <dd className="text-right font-mono tabular-nums text-slate-800">{fmtInt(r.count)}</dd>
          </div>
        ))}
      </dl>
    );
  }, [report]);

  const salariesKpiDetail = useMemo(() => {
    if (!report) return null;
    const rows = [
      { label: 'Permanent', amount: report.totalPermSalaries },
      { label: 'Contractual', amount: report.totalContSalaries },
      { label: 'Administrative', amount: report.totalAdminSalaries },
    ];
    return (
      <dl className="space-y-1.5 leading-relaxed">
        {rows.map((r) => (
          <div key={r.label} className="flex justify-between gap-3">
            <dt className="shrink-0 text-slate-500">{r.label}</dt>
            <dd className="text-right font-mono tabular-nums text-slate-800">{fmtUsd(r.amount)}</dd>
          </div>
        ))}
      </dl>
    );
  }, [report]);

  const budgetSubmittedRatePct = useMemo(() => {
    if (!report || report.schoolCount <= 0) return null;
    return sharePct(report.submittedSchoolCount, report.schoolCount);
  }, [report]);

  const budgetSubmittedKpiDetail = useMemo(() => {
    if (!report) return null;
    return (
      <p className="text-slate-600">
        <span className="font-mono tabular-nums text-slate-800">{fmtInt(report.submittedSchoolCount)}</span> of{' '}
        <span className="font-mono tabular-nums text-slate-800">{fmtInt(report.schoolCount)}</span> schools in scope have
        submitted an approved budget.
      </p>
    );
  }, [report]);

  const schoolsNationalSharePct = useMemo(() => {
    if (!report) return null;
    return sharePct(report.schoolCount, report.totalSchoolCount);
  }, [report]);

  const schoolsKpiDetail = useMemo(() => {
    if (!report) return null;
    const pct = schoolsNationalSharePct;
    return (
      <p className="text-slate-600">
        <span className="font-mono tabular-nums text-slate-800">{fmtInt(report.schoolCount)}</span> of{' '}
        <span className="font-mono tabular-nums text-slate-800">{fmtInt(report.totalSchoolCount)}</span> national
        schools
        {pct != null ? (
          <>
            {' '}
            (<span className="font-mono tabular-nums text-slate-800">{fmtPct1(pct)}</span>)
          </>
        ) : null}
        .
      </p>
    );
  }, [report, schoolsNationalSharePct]);

  const enrollmentBandKpiDetail = useMemo(
    () => (report ? buildBandKpiDetail(report.schoolBands, report.schoolCount) : null),
    [report]
  );

  const tuitionBandKpiDetail = useMemo(
    () => (report ? buildBandKpiDetail(report.tuitionBands, report.schoolCount) : null),
    [report]
  );

  const parentTuitionBandKpiDetail = useMemo(
    () => (report ? buildBandKpiDetail(report.parentTuitionBands, report.schoolCount) : null),
    [report]
  );

  return (
    <div className="mx-auto max-w-[1400px] space-y-6 px-4 pb-16 pt-8 sm:px-6 lg:px-8">
      <PageHeader
        title="Regional comparisons"
        description="Governorate-level overview of enrollment, tuition, budget submission, and sector finances for the selected year."
      />

      <section
        aria-labelledby="regional-filters-heading"
        className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm ring-1 ring-slate-100 sm:p-6"
      >
        <h2 id="regional-filters-heading" className="sr-only">
          Report filters
        </h2>
        <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end">
          <div className="min-w-[10rem] flex-1 sm:max-w-xs">
            <label htmlFor="regional-year" className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-700">
              <GraduationCap className="size-4 text-indigo-600" aria-hidden />
              Reporting year <span className="text-red-600">*</span>
            </label>
            <select
              id="regional-year"
              value={year}
              onChange={(e) => {
                setYear(e.target.value);
                setGovernorateIds([]);
                setCityIds([]);
                setAreaIds([]);
              }}
              disabled={reportYearsQuery.isPending && budgetYearOptions.length === 0}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            >
              <option value="">Select reporting year…</option>
              {budgetYearOptions.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.label}
                </option>
              ))}
            </select>
            {reportYearsQuery.isError ? (
              <p className="mt-2 text-xs text-rose-700">
                {reportYearsQuery.error instanceof Error
                  ? reportYearsQuery.error.message
                  : 'Could not load reporting years.'}
              </p>
            ) : null}
            {reportYearsQuery.isPending && budgetYearOptions.length === 0 ? (
              <p className="mt-2 text-xs text-slate-500">Loading years…</p>
            ) : null}
            {!reportYearsQuery.isPending && !reportYearsQuery.isError && budgetYearOptions.length === 0 ? (
              <p className="mt-2 text-xs font-medium text-amber-800">No reporting years returned for your account.</p>
            ) : null}
          </div>
          <div className="min-w-[12rem] flex-1 sm:max-w-md">
            <label htmlFor="regional-gov" className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-600">
              <MapPin className="size-4 text-indigo-600" aria-hidden />
              Governorates
            </label>
            <Select
              id="regional-gov"
              mode="multiple"
              allowClear
              disabled={!yearSelected}
              placeholder={yearSelected ? 'All governorates' : 'Select a year first'}
              className="w-full"
              options={governorateSelectOptions}
              value={governorateIds}
              onChange={(v) => setGovernorateIds(v)}
              loading={governoratesQuery.isPending}
              maxTagCount="responsive"
              optionFilterProp="label"
              aria-label="Filter by governorate"
            />
          </div>
          <div className="min-w-[12rem] flex-1 sm:max-w-md">
            <label htmlFor="regional-city" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-600">
              Cities
            </label>
            <Select
              id="regional-city"
              mode="multiple"
              allowClear
              disabled={!yearSelected || governorateIds.length === 0}
              placeholder={
                !yearSelected
                  ? 'Select a year first'
                  : governorateIds.length === 0
                    ? 'Select governorate(s) first'
                    : 'All cities in selection'
              }
              className="w-full"
              options={citySelectOptions}
              value={cityIds}
              onChange={(v) => setCityIds(v)}
              loading={citiesQuery.isLoading}
              maxTagCount="responsive"
              optionFilterProp="label"
              aria-label="Filter by city"
            />
          </div>
          <div className="min-w-[12rem] flex-1 sm:max-w-md">
            <label htmlFor="regional-area" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-600">
              Areas
            </label>
            <Select
              id="regional-area"
              mode="multiple"
              allowClear
              disabled={!yearSelected || governorateIds.length === 0 || cityIds.length === 0}
              placeholder={
                !yearSelected
                  ? 'Select a year first'
                  : governorateIds.length === 0
                    ? 'Select governorate(s) first'
                    : cityIds.length === 0
                      ? 'Select city/cities first'
                      : 'All areas in selection'
              }
              className="w-full"
              options={areaSelectOptions}
              value={areaIds}
              onChange={(v) => setAreaIds(v)}
              loading={areasQuery.isLoading}
              maxTagCount="responsive"
              optionFilterProp="label"
              aria-label="Filter by area"
            />
          </div>
        </div>
        {governoratesQuery.isError ? (
          <Alert
            type="error"
            showIcon
            className="mt-3"
            message="Could not load governorates"
            description={
              governoratesQuery.error instanceof Error
                ? governoratesQuery.error.message
                : 'Please try again later.'
            }
          />
        ) : null}
        {citiesQuery.isError ? (
          <Alert
            type="warning"
            showIcon
            className="mt-3"
            message="Could not load cities"
            description={
              citiesQuery.error instanceof Error ? citiesQuery.error.message : 'Please try again later.'
            }
          />
        ) : null}
        {areasQuery.isError ? (
          <Alert
            type="warning"
            showIcon
            className="mt-3"
            message="Could not load areas"
            description={
              areasQuery.error instanceof Error ? areasQuery.error.message : 'Please try again later.'
            }
          />
        ) : null}
      </section>

      {!yearSelected ? (
        <RegionalComparisonsYearLanding />
      ) : (
        <>
      {reportQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message="Could not load regional comparisons"
          description={
            reportQuery.error instanceof Error
              ? reportQuery.error.message
              : 'Please try again later.'
          }
        />
      ) : null}

      <section aria-labelledby="regional-kpi-heading">
        <h2 id="regional-kpi-heading" className="mb-3 text-lg font-semibold tracking-tight text-slate-900">
          Overview
        </h2>
        <div className="relative grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {isReportLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/75 backdrop-blur-[1px]">
              <Loader2 className="size-10 animate-spin text-indigo-600" aria-hidden />
              <span className="sr-only">Loading regional comparisons</span>
            </div>
          )}
          <KpiCard
            label="Schools"
            value={report != null ? fmtInt(report.schoolCount) : '—'}
            sublabel={
              schoolsNationalSharePct != null
                ? `${fmtPct1(schoolsNationalSharePct)} of national sector`
                : 'In selected scope'
            }
            detail={schoolsKpiDetail}
            icon={Building2Icon}
            accent="indigo"
          />
          <KpiCard
            label="Students"
            value={report != null ? fmtInt(report.totalStudentsCount) : '—'}
            sublabel="Enrolled in scope"
            icon={Users}
            accent="emerald"
          />
          <KpiCard
            label="Scholarship students"
            value={report != null ? fmtInt(report.totalScholarshipStudentsCount) : '—'}
            sublabel="In selected scope"
            icon={GraduationCap}
            accent="amber"
          />
          <KpiCard
            label="Teachers"
            value={report != null ? fmtInt(report.totalTeacherCount) : '—'}
            sublabel="Permanent, contractual, administrative"
            detail={teacherCountKpiDetail}
            icon={Users}
            accent="emerald"
          />
          <KpiCard
            label="Teacher salaries"
            value={report != null ? fmtUsd(report.totalSalaries) : '—'}
            sublabel="USD (annualized)"
            detail={salariesKpiDetail}
            icon={DollarSignIcon}
            accent="amber"
          />
          <KpiCard
            label="Reported revenue"
            value={report != null ? fmtUsd(report.totalRevenueUsd) : '—'}
            sublabel="USD from submitted budgets"
            icon={CheckCircleIcon}
            accent="rose"
          />
          <KpiCard
            label="Budget submission rate"
            value={budgetSubmittedRatePct != null ? fmtPct1(budgetSubmittedRatePct) : '—'}
            sublabel="Schools in scope"
            detail={budgetSubmittedKpiDetail}
            icon={CheckCircleIcon}
            accent="emerald"
          />
          <KpiCard
            label="Enrollment size bands"
            value={report != null ? bandKpiHeadline(report.schoolBands) : '—'}
            sublabel={
              report != null && report.schoolBands.length > 0
                ? `${report.schoolBands.length} bands`
                : 'Enrollment bands'
            }
            detail={enrollmentBandKpiDetail}
            icon={Building2Icon}
            accent="indigo"
          />
          <KpiCard
            label="Tuition bands"
            value={report != null ? bandKpiHeadline(report.tuitionBands) : '—'}
            sublabel={
              report != null && report.tuitionBands.length > 0
                ? `${report.tuitionBands.length} bands`
                : 'Tuition clusters'
            }
            detail={tuitionBandKpiDetail}
            icon={Building2Icon}
            accent="amber"
          />
          <KpiCard
            label="Parent fee bands"
            value={report != null ? bandKpiHeadline(report.parentTuitionBands) : '—'}
            sublabel={
              report != null && report.parentTuitionBands.length > 0
                ? `${report.parentTuitionBands.length} bands`
                : 'Parent tuition bands'
            }
            detail={parentTuitionBandKpiDetail}
            icon={Building2Icon}
            accent="rose"
          />
        </div>
      </section>

      <section aria-labelledby="regional-table-heading" className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <h2 id="regional-table-heading" className="text-lg font-semibold tracking-tight text-slate-900">
            Governorate comparison
          </h2>
          <p className="max-w-xl text-xs text-slate-500">
            Key indicators by governorate for year {year}.
          </p>
        </div>
        <div className="relative overflow-x-auto rounded-2xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-100">
          {isReportLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/80">
              <Loader2 className="size-8 animate-spin text-indigo-600" aria-hidden />
            </div>
          )}
          <table className="min-w-[960px] w-full border-collapse text-left text-sm">
            <caption className="sr-only">
              Governorate comparison for reporting year {year}
            </caption>
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100 text-slate-800">
                <th scope="col" className="px-3 py-3">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleSort('governorateName')}
                  >
                    Governorate
                    {sortKey === 'governorateName' ? (
                      sortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleSort('schoolsCount')}
                  >
                    Schools
                    {sortKey === 'schoolsCount' ? (
                      sortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleSort('studentsCount')}
                  >
                    Students
                    {sortKey === 'studentsCount' ? (
                      sortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleSort('avgTuitionUsdWeighted')}
                  >
                    Avg tuition (USD)
                    {sortKey === 'avgTuitionUsdWeighted' ? (
                      sortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleSort('minTuitionUsd')}
                  >
                    Min tuition
                    {sortKey === 'minTuitionUsd' ? (
                      sortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleSort('maxTuitionUsd')}
                  >
                    Max tuition
                    {sortKey === 'maxTuitionUsd' ? (
                      sortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleSort('budgetSubmittedRatePct')}
                  >
                    Budget submitted
                    {sortKey === 'budgetSubmittedRatePct' ? (
                      sortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {!isReportLoading &&
                sortedRows.map((row) => (
                  <tr key={row.rowId} className="border-b border-slate-100 hover:bg-slate-50/80">
                    <th scope="row" className="px-3 py-2.5 font-medium text-slate-900" dir="auto">
                      {row.governorateName}
                    </th>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-800">{fmtInt(row.schoolsCount)}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-800">{fmtInt(row.studentsCount)}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-900">{fmtUsd(row.avgTuitionUsdWeighted)}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">{fmtUsd(row.minTuitionUsd)}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">{fmtUsd(row.maxTuitionUsd)}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-800">{fmtPct1(row.budgetSubmittedRatePct)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!isReportLoading && sortedRows.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-slate-500">No governorate data for the current filters.</p>
          ) : null}
        </div>
      </section>

      <section aria-labelledby="band-table-heading" className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <h2 id="band-table-heading" className="text-lg font-semibold tracking-tight text-slate-900">
              {bandTableSectionCopy.title}
            </h2>
            <p className="mt-1 max-w-2xl text-xs text-slate-500">{bandTableSectionCopy.description}</p>
          </div>
          <div className="min-w-[12rem]">
            <label htmlFor="band-kind" className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-600">
              Band type
            </label>
            <select
              id="band-kind"
              value={bandKind}
              onChange={(e) => setBandKind(e.target.value as BandKind)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            >
              {(Object.keys(BAND_KIND_LABELS) as BandKind[]).map((k) => (
                <option key={k} value={k}>
                  {BAND_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="relative overflow-x-auto rounded-2xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-100">
          {isReportLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/80">
              <Loader2 className="size-8 animate-spin text-indigo-600" aria-hidden />
            </div>
          )}
          <table className="min-w-[1100px] w-full border-collapse text-left text-sm">
            <caption className="sr-only">
              {BAND_KIND_LABELS[bandKind]} by governorate for year {year}
            </caption>
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100 text-slate-800">
                <th scope="col" className="px-3 py-3">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleBandSort('governorateName')}
                  >
                    Governorate
                    {bandSortKey === 'governorateName' ? (
                      bandSortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleBandSort('bandLabel')}
                  >
                    Band
                    {bandSortKey === 'bandLabel' ? (
                      bandSortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleBandSort('studentsCount')}
                  >
                    Students
                    {bandSortKey === 'studentsCount' ? (
                      bandSortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleBandSort('studentShareOfGovernoratePct')}
                  >
                    % of gov. students
                    {bandSortKey === 'studentShareOfGovernoratePct' ? (
                      bandSortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleBandSort('schoolsCount')}
                  >
                    Schools
                    {bandSortKey === 'schoolsCount' ? (
                      bandSortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleBandSort('avgTuitionUsdWeighted')}
                  >
                    Avg tuition (USD)
                    {bandSortKey === 'avgTuitionUsdWeighted' ? (
                      bandSortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                <th scope="col" className="px-3 py-3 text-right">
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                    onClick={() => toggleBandSort('budgetSubmittedRatePct')}
                  >
                    Budget submitted
                    {bandSortKey === 'budgetSubmittedRatePct' ? (
                      bandSortDir === 'asc' ? (
                        <ArrowUp className="size-3.5" aria-hidden />
                      ) : (
                        <ArrowDown className="size-3.5" aria-hidden />
                      )
                    ) : null}
                  </button>
                </th>
                {showBandComplianceColumn ? (
                  <th scope="col" className="px-3 py-3 text-right">
                    <button
                      type="button"
                      className="ml-auto inline-flex items-center gap-1 font-semibold hover:text-indigo-700"
                      onClick={() => toggleBandSort('complianceRatePct')}
                    >
                      Compliance
                      {bandSortKey === 'complianceRatePct' ? (
                        bandSortDir === 'asc' ? (
                          <ArrowUp className="size-3.5" aria-hidden />
                        ) : (
                          <ArrowDown className="size-3.5" aria-hidden />
                        )
                      ) : null}
                    </button>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {!isReportLoading &&
                bandTableRows.map((row) => (
                  <tr
                    key={row.rowId}
                    className={
                      row.isSummary
                        ? 'border-b border-slate-200 bg-slate-50/90 font-medium'
                        : 'border-b border-slate-100 hover:bg-slate-50/80'
                    }
                  >
                    <th
                      scope="row"
                      className={`px-3 py-2.5 text-slate-900 ${row.isSummary ? 'font-semibold' : 'font-medium'}`}
                      dir="auto"
                    >
                      {row.isSummary ? '' : row.governorateName}
                    </th>
                    <td
                      className={`max-w-[20rem] px-3 py-2.5 ${row.isSummary ? 'font-semibold text-slate-900' : 'text-slate-800'}`}
                    >
                      {row.isSummary ? (
                        <span className="text-slate-600">
                          {row.governorateName} — {row.bandLabel}
                        </span>
                      ) : (
                        row.bandLabel
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-800">{fmtInt(row.studentsCount)}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">
                      {fmtGovernorateStudentSharePct(row.studentShareOfGovernoratePct)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-800">{fmtInt(row.schoolsCount)}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-900">{fmtUsd(row.avgTuitionUsdWeighted)}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-800">
                      {fmtPct1(row.budgetSubmittedRatePct)}
                    </td>
                    {showBandComplianceColumn ? (
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-800">
                        {fmtPct1(row.complianceRatePct)}
                      </td>
                    ) : null}
                  </tr>
                ))}
            </tbody>
          </table>
          {!isReportLoading && bandTableRows.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-slate-500">
              No {BAND_KIND_LABELS[bandKind].toLowerCase()} cross-tab data for the current filters.
            </p>
          ) : null}
        </div>
      </section>

      <section aria-labelledby="regional-finance-heading" className="space-y-2">
        <h2 id="regional-finance-heading" className="text-lg font-semibold tracking-tight text-slate-900">
          Financial summary
        </h2>
        <p className="text-xs text-slate-500">Sector totals from submitted school budgets (USD).</p>
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            {
              title: 'Revenue',
              value: report != null ? fmtUsd(report.totalRevenueUsd) : '—',
              foot: 'Submitted budgets in scope',
            },
            {
              title: 'Expenditure',
              value: report != null ? fmtUsd(report.totalExpenseUsd) : '—',
              foot: 'Submitted budgets in scope',
            },
            {
              title: 'Avg. teacher salary',
              value: avgTeacherSalaryUsd != null ? fmtUsd(avgTeacherSalaryUsd) : '—',
              foot: 'Salaries ÷ teacher count',
            },
          ].map((card) => (
            <div
              key={card.title}
              className="rounded-2xl border border-slate-200/80 bg-white px-4 py-5 text-center shadow-sm ring-1 ring-slate-100"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{card.title}</p>
              <p className="mt-3 font-mono text-2xl font-bold text-slate-900">{card.value}</p>
              <p className="mt-2 text-xs text-slate-500">{card.foot}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section
          aria-labelledby="chart-tuition-heading"
          className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm ring-1 ring-slate-100"
        >
          <h2 id="chart-tuition-heading" className="text-base font-semibold text-slate-900">
            Average tuition by governorate
          </h2>
          <p className="mt-1 text-xs text-slate-500">Student-weighted average tuition (USD). Dashed line is the scope average.</p>
          <div className="mt-4 h-[320px] w-full min-w-0">
            {isReportLoading || chartTuitionData.length === 0 ? (
              <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-slate-200 text-slate-400">
                {isReportLoading ? <Loader2 className="size-8 animate-spin text-indigo-600" /> : 'No chart data for this view.'}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={chartTuitionData}
                  margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => `$${Math.round(v / 1000)}k`} tick={{ fontSize: 11 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={108}
                    tick={{ fontSize: 11 }}
                    interval={0}
                  />
                  <RechartsTooltip
                    formatter={(value: number) => [fmtUsd(value), 'Avg tuition']}
                    labelFormatter={(label) => String(label)}
                  />
                  <ReferenceLine
                    x={nationalAvgTuition}
                    stroke="#64748b"
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    label={{ value: 'Scope avg', position: 'top', fill: '#64748b', fontSize: 11 }}
                  />
                  <Bar dataKey="avgTuition" name="Avg tuition (USD)" fill="#4f46e5" radius={[0, 4, 4, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        <section
          aria-labelledby="chart-students-heading"
          className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm ring-1 ring-slate-100"
        >
          <h2 id="chart-students-heading" className="text-base font-semibold text-slate-900">
            Students by governorate
          </h2>
          <p className="mt-1 text-xs text-slate-500">Total enrolled students by governorate.</p>
          <div className="mt-4 h-[320px] w-full min-w-0">
            {isReportLoading || chartStudentsData.length === 0 ? (
              <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-slate-200 text-slate-400">
                {isReportLoading ? <Loader2 className="size-8 animate-spin text-indigo-600" /> : 'No chart data for this view.'}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartStudentsData} margin={{ top: 8, right: 12, left: 4, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-28} textAnchor="end" height={56} />
                  <YAxis tickFormatter={(v) => fmtInt(Number(v))} tick={{ fontSize: 11 }} width={56} />
                  <RechartsTooltip
                    formatter={(value: number) => [fmtInt(value), 'Students']}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.fullName != null ? String(payload[0].payload.fullName) : ''
                    }
                  />
                  <Bar dataKey="students" name="Students" fill="#0d9488" radius={[4, 4, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>
      </div>

      </>
      )}
    </div>
  );
}
